import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Snowflake, Ship, Mountain, Utensils, Waves, Plane, Compass } from "lucide-react";
import { api, formatISK } from "@/lib/api";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const icons = {
  amphibian: Ship,
  zodiac: Ship,
  kayak: Waves,
  ice_cave: Snowflake,
  snowmobile: Snowflake,
  super_jeep_glacier: Mountain,
  super_jeep_black_beach: Mountain,
  super_jeep_highlands: Mountain,
  horseback: Compass,
  buggy: Compass,
  helicopter: Plane,
};

export default function Home() {
  const [tours, setTours] = useState([]);
  const location = useLocation();

  useEffect(() => {
    api.get("/tours").then((res) => setTours(res.data.tours)).catch(() => {});
  }, []);

  useEffect(() => {
    if (location.hash === "#the-table") {
      const scrollToTable = () => document.getElementById("the-table")?.scrollIntoView({ behavior: "smooth", block: "start" });
      [100, 500, 900].forEach((d) => setTimeout(scrollToTable, d));
    }
  }, [location]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[88vh] min-h-[560px] flex items-end overflow-hidden">
        <img
          src="https://immersionfestival.com/assets/VIDEO-POSTER.jpg"
          alt="Iceland landscape"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-[#050505]/30" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-20 w-full">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }}
            className="text-xs uppercase tracking-[0.35em] text-gray-300 mb-6"
          >
            Iceland · October 1–5, 2026 · Viking Village
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-display font-extrabold uppercase tracking-tighter text-5xl sm:text-6xl lg:text-[6rem] leading-[0.92] max-w-4xl"
          >
            Beyond the festival, into Iceland.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2 }}
            className="text-gray-300 text-base sm:text-lg mt-8 max-w-xl leading-relaxed"
          >
            Journey through glacial lagoons, wild highlands and some of Iceland's
            most extraordinary landscapes. Gather for an intimate three-course
            dining experience created by Icelandic chefs. Curated exclusively for
            Immersion Festival guests.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.35 }}
            className="flex flex-wrap gap-4 mt-10"
          >
            <Link to="/tours" data-testid="hero-tours-btn" className="bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-4 px-8 transition-colors inline-flex items-center gap-3">
              Explore Tours <ArrowRight size={16} />
            </Link>
            <button
              type="button"
              data-testid="hero-dining-btn"
              onClick={() => document.getElementById("the-table")?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="bg-transparent text-white border border-white/25 hover:border-white/60 uppercase tracking-[0.15em] text-xs font-bold py-4 px-8 transition-all inline-flex items-center gap-3"
            >
              Discover Dining <Utensils size={16} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* INTRO */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 border-b border-white/10">
        <motion.div {...fadeUp} className="grid md:grid-cols-12 gap-10 items-end">
          <h2 className="md:col-span-8 font-display font-bold uppercase tracking-tighter text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
            Raw. Quiet. Unpredictable. Iceland isn't the backdrop. It's the experience.
          </h2>
          <p className="md:col-span-4 text-gray-400 leading-relaxed text-sm">
            Led by local specialists in small groups, each expedition takes you
            deeper into Iceland's extraordinary landscape. Tours depart from the
            Viking Café and return to the village, with multiple days available
            throughout the festival.
          </p>
        </motion.div>
      </section>

      {/* TOURS PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <motion.div {...fadeUp} className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gray-500 mb-3">The Expeditions</p>
            <h2 className="font-display font-bold uppercase tracking-tighter text-3xl sm:text-4xl">Adventure Tours</h2>
          </div>
          <Link to="/tours" data-testid="view-all-tours" className="text-xs uppercase tracking-[0.2em] text-gray-400 hover:text-white inline-flex items-center gap-2 transition-colors">
            View all <ArrowRight size={14} />
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {tours.map((t, i) => {
            const Icon = icons[t.id] || Mountain;
            const CardInner = (
              <>
                <img src={t.image} alt={t.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent" />
                {t.coming_soon && (
                  <span className="absolute top-5 right-5 z-20 bg-black/70 backdrop-blur border border-white/20 text-white text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5">Coming Soon</span>
                )}
                <div className="relative z-10 h-full flex flex-col justify-end p-8">
                  <Icon className="text-white/80 mb-4" size={22} />
                  <h3 className="font-display font-bold uppercase tracking-tight text-2xl leading-tight">{t.name}</h3>
                  <p className="text-gray-300 text-sm mt-2">{t.tagline}</p>
                  <div className="flex items-center justify-between mt-5 pt-5 border-t border-white/15">
                    <span className="text-xs uppercase tracking-[0.15em] text-gray-400">From {formatISK(t.has_child ? t.child_price : t.adult_price)}</span>
                    {t.coming_soon ? (
                      <span className="text-xs uppercase tracking-[0.15em] text-gray-400">Coming Soon</span>
                    ) : (
                      <span className="text-xs uppercase tracking-[0.15em] text-white inline-flex items-center gap-2 group-hover:gap-3 transition-all">Book <ArrowRight size={14} /></span>
                    )}
                  </div>
                </div>
              </>
            );
            return (
              <motion.div key={t.id} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}>
                {t.coming_soon ? (
                  <div data-testid={`home-tour-card-${t.id}`} className="group block relative overflow-hidden border border-white/10 h-[380px] cursor-default">
                    {CardInner}
                  </div>
                ) : (
                  <Link
                    to={`/tours/${t.id}`}
                    data-testid={`home-tour-card-${t.id}`}
                    className="group block relative overflow-hidden border border-white/10 hover:border-white/30 transition-all duration-500 h-[380px]"
                  >
                    {CardInner}
                  </Link>
                )}
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* DINING CTA */}
      <section id="the-table" className="relative overflow-hidden border-t border-white/10 scroll-mt-20">
        <img src="https://static.prod-images.emergentagent.com/jobs/44292365-21c1-4f3d-88e3-fd6ad71c5275/images/8fcfe87b78cd7fdb9ccfeaad4cd1dc30fd9f3497b6f429e1e55f5c372bc1e5ff.png" alt="Fine dining inside a black stretch tent" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#050505]/75" />
        <motion.div {...fadeUp} className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-28 md:py-36">
          <p className="text-xs uppercase tracking-[0.3em] text-gray-300 mb-5">The Table · Dinner Nightly</p>
          <h2 className="font-display font-extrabold uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl max-w-2xl leading-[0.95]">
            An intimate three-course dining experience.
          </h2>
          <p className="text-gray-300 mt-6 max-w-lg leading-relaxed">
            A thoughtfully curated three-course menu created by Icelandic chefs,
            served in an intimate setting with limited seating. Join us for dinner
            at 6:00 PM or 9:00 PM each evening.
          </p>
          <Link to="/dining" data-testid="dining-cta-btn" className="mt-10 inline-flex items-center gap-3 bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-4 px-8 transition-colors">
            Reserve a Table <ArrowRight size={16} />
          </Link>
        </motion.div>
      </section>

      {/* LOCAL PARTNER */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 border-t border-white/10">
        <motion.div {...fadeUp} className="grid md:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gray-500 mb-4">Our Local Experience Partner</p>
            <h2 className="font-display font-bold uppercase tracking-tighter text-3xl sm:text-4xl leading-[1.05] mb-6">Exclusive Reykjavík</h2>
            <p className="text-gray-400 leading-relaxed text-sm max-w-xl">
              Exclusive Reykjavík is Immersion Festival's local partner for tours
              and guest experiences. Led by Víkingur Heiðar Arnórsson, the team
              brings more than two decades of experience across Icelandic travel
              and hospitality — delivering end-to-end bespoke luxury for elite
              clientele and celebrities from touchdown to departure. From large-scale
              events to never-before-seen experiences tailored to each client's
              vision, their local knowledge and trusted network help ensure each
              experience is carefully selected, smoothly coordinated and supported
              from departure through return.
            </p>
          </div>
          <div data-testid="chef-bio" className="border border-white/10 bg-[#0C0C0E] p-8 md:p-10">
            <p className="text-xs uppercase tracking-[0.25em] text-gray-500 mb-4">Meet the Chef</p>
            <h3 className="font-display font-bold uppercase tracking-tighter text-3xl mb-2 leading-[1.05]">Bjarki Snær Þorsteinsson</h3>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-6">Award-Winning Icelandic Chef</p>
            <p className="text-gray-400 leading-relaxed text-sm mb-4">
              Bjarki Snær Þorsteinsson is an award-winning Icelandic chef and former member of the
              Icelandic National Culinary Team. With years of experience creating exclusive dining
              experiences for private clients, luxury events, and international guests, his cooking
              celebrates the purity of Icelandic ingredients with refined Nordic techniques.
            </p>
            <p className="text-gray-400 leading-relaxed text-sm">
              At Immersion Festival, Bjarki invites guests on a culinary journey through Iceland — where
              every course tells a story of the country's landscapes, traditions, and wild nature. Expect
              an intimate tasting experience that is elegant, authentic, and uniquely Icelandic.
            </p>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
